// Form pieces shared by the sign-in steps and the save sheet.

import { CODE_LENGTH, extractCode } from "@aybuben/client/auth/flow.ts";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { useStrings, type UiKey } from "../i18n/index.tsx";
import { COL, FOCUS } from "../theme.ts";

interface FieldProps {
	label: string;
	type: "email";
	value: string;
	onChange: (v: string) => void;
	autoComplete: string;
	hint?: string;
	/** The id of the error line describing this field, when there is one. */
	errorId?: string;
}

export function Field({ label, type, value, onChange, autoComplete, hint, errorId }: FieldProps) {
	const hintId = useId();
	const describedBy = [hint ? hintId : "", errorId ?? ""].filter(Boolean).join(" ") || undefined;
	return (
		<label className="mt-4 block">
			<span className="text-sm font-semibold" style={{ color: COL.mute }}>
				{label}
			</span>
			<input
				aria-label={label}
				type={type}
				inputMode="email"
				value={value}
				autoComplete={autoComplete}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={describedBy}
				onChange={(e) => onChange(e.target.value)}
				className={`mt-1 w-full rounded-2xl px-4 py-3 font-semibold ${FOCUS}`}
				style={{
					background: COL.card,
					border: `2px solid ${errorId ? COL.red : COL.line}`,
					color: COL.ink,
				}}
			/>
			{hint && (
				<span id={hintId} className="mt-1 block text-xs" style={{ color: COL.mute }}>
					{hint}
				</span>
			)}
		</label>
	);
}

export const ErrLine = ({ id, k }: { id: string; k: UiKey | null }) => {
	const t = useStrings();
	if (!k) return null;
	return (
		<p id={id} className="mt-3 text-sm font-semibold" role="alert" style={{ color: COL.red }}>
			{t(k)}
		</p>
	);
};

interface LinkBtnProps {
	onClick: () => void;
	children: ReactNode;
	disabled?: boolean;
}

export const LinkBtn = ({ onClick, children, disabled }: LinkBtnProps) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		className={`mt-3 w-full rounded-lg py-2 text-sm font-semibold ${FOCUS}`}
		style={{ color: disabled ? COL.faint : COL.lapis, cursor: disabled ? "default" : "pointer" }}
	>
		{children}
	</button>
);

export const Divider = ({ label }: { label: string }) => (
	<div
		className="my-5 flex items-center gap-3 text-sm"
		role="separator"
		aria-label={label}
		style={{ color: COL.faint }}
	>
		<span className="h-px flex-1" style={{ background: COL.line }} />
		<span aria-hidden>{label}</span>
		<span className="h-px flex-1" style={{ background: COL.line }} />
	</div>
);

interface GoogleButtonProps {
	onClick: () => void;
	disabled: boolean;
	children: ReactNode;
}

export const GoogleButton = ({ onClick, disabled, children }: GoogleButtonProps) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		className={`mt-5 flex w-full items-center justify-center gap-3 rounded-2xl px-4 py-4 text-lg font-semibold ${FOCUS}`}
		style={{
			background: COL.card,
			border: `2px solid ${COL.line}`,
			color: COL.ink,
			opacity: disabled ? 0.6 : 1,
			cursor: disabled ? "not-allowed" : "pointer",
		}}
	>
		{GOOGLE_SVG}
		<span>{children}</span>
	</button>
);

const GOOGLE_SVG = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M15.7034 7.91133C14.7554 7.02509 13.4903 6.54228 12.1813 6.56212C9.78605 6.56212 7.75176 8.14611 7.02642 10.279V10.2791C6.64183 11.3968 6.64183 12.6071 7.02642 13.7249H7.02979C7.75849 15.8545 9.78941 17.4385 12.1847 17.4385C13.4211 17.4385 14.4826 17.1285 15.3053 16.5809V16.5787C16.2735 15.9504 16.9348 14.9616 17.1406 13.8439H12.1813V10.3783H20.8414C20.9494 10.9802 21 11.5952 21 12.207C21 14.9443 20.002 17.2586 18.2655 18.826L18.2673 18.8274C16.7458 20.203 14.6576 21 12.1813 21C8.70985 21 5.53527 19.082 3.97666 16.043V16.043C2.67445 13.5 2.67445 10.5039 3.97666 7.96096H3.97668L3.97666 7.96094C5.53527 4.9186 8.70985 3.00061 12.1813 3.00061C14.4619 2.97415 16.6649 3.8141 18.3247 5.34188L15.7034 7.91133Z"
			fill={COL.pom}
		/>
	</svg>
);

interface OtpInputProps {
	label: string;
	value: string;
	onChange: (v: string) => void;
	onComplete: (code: string) => void;
	disabled?: boolean;
	errorId?: string;
}

/**
 * One real input under six drawn boxes: paste, email/SMS autofill and screen
 * readers all see a single field, which six separate inputs break.
 */
export function OtpInput({ label, value, onChange, onComplete, disabled, errorId }: OtpInputProps) {
	const ref = useRef<HTMLInputElement>(null);
	// This screen exists to take one thing.
	useEffect(() => {
		ref.current?.focus();
	}, []);
	const accept = (raw: string) => {
		const v = extractCode(raw);
		onChange(v);
		if (v.length === CODE_LENGTH) onComplete(v);
	};
	return (
		<div className="relative mt-5 rounded-xl focus-within:outline focus-within:outline-4 focus-within:outline-offset-2 focus-within:outline-blue-400">
			<input
				ref={ref}
				aria-label={label}
				value={value}
				inputMode="numeric"
				autoComplete="one-time-code"
				disabled={disabled}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				onChange={(e) => accept(e.target.value)}
				onPaste={(e) => {
					e.preventDefault();
					accept(e.clipboardData.getData("text"));
				}}
				className="absolute inset-0 h-full w-full cursor-text opacity-0"
			/>
			<div aria-hidden className="pointer-events-none flex gap-2">
				{Array.from({ length: CODE_LENGTH }, (_, i) => (
					<span
						key={i}
						className="flex h-14 flex-1 items-center justify-center rounded-xl text-2xl font-extrabold"
						style={{
							background: COL.card,
							border: `2px solid ${i === value.length ? COL.pom : COL.line}`,
							color: COL.ink,
						}}
					>
						{value[i] ?? ""}
					</span>
				))}
			</div>
		</div>
	);
}
